import { parseInstructions, parseValues } from '../model/instructions';
export type { SetupInstructions } from '../model/instructions';

export type SetupView = {
  expiresAt: string;
  configKinds: string[];
};

export class SetupLinkError extends Error {
  constructor(public readonly expired: boolean) {
    super(expired ? 'Setup link expired' : 'Setup link invalid');
  }
}

export class SetupInstructionsError extends Error {
  constructor() {
    super('Setup instructions unavailable');
  }
}

export const loadSetupInstructions = async (locale: 'ru' | 'en') => {
  const response = await fetch(
    `/api/setup/instructions?locale=${encodeURIComponent(locale)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
    }
  );
  if (!response.ok) {
    throw new SetupInstructionsError();
  }
  const instructions: unknown = await response.json();
  const parsed = parseInstructions(instructions, locale);
  if (!parsed) {
    throw new SetupInstructionsError();
  }
  return parsed;
};

export const resolveSetup = async (token: string): Promise<SetupView> => {
  const response = await fetch('/api/setup/resolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (response.status === 404 || response.status === 410) {
    throw new SetupLinkError(true);
  }
  if (!response.ok) {
    throw new SetupLinkError(false);
  }
  return response.json() as Promise<SetupView>;
};

export const downloadSetupConfig = async (token: string, kind: string) => {
  const response = await fetch(
    `/api/setup/config/${encodeURIComponent(kind)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }
  );

  if (response.status === 404 || response.status === 410) {
    throw new SetupLinkError(true);
  }
  if (!response.ok) {
    throw new SetupLinkError(false);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `private-gate-${kind}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

export const loadSetupValues = async (token: string) => {
  const response = await fetch('/api/setup/values', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
    cache: 'no-store',
  });
  if (!response.ok)
    throw new SetupLinkError(
      response.status === 404 || response.status === 410
    );
  return parseValues(await response.json());
};
